import { NgClass } from "@angular/common";
import { Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnDestroy, Output, ViewChild } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { NogoshIconPipe } from "../icons/nogosh-icon.pipe";
import { LoadingProgressComponent } from "../loading-progress/loading-progress.component";

@Component({
  selector: "app-screen-shell",
  standalone: true,
  imports: [NgClass, TranslateModule, NogoshIconPipe, LoadingProgressComponent],
  templateUrl: "./screen-shell.component.html",
  styleUrls: ["./screen-shell.component.css"]
})
export class ScreenShellComponent implements OnDestroy {
  @Input() public contentClass: string | string[] | Set<string> | {[klass: string]: any} = "container";
  @Input() public contentWidth: "custom" | "standard" = "custom";
  @Input() public contentInset: "none" | "page" = "none";
  @Input() public layout: "default" | "public-entry" | "full-height" = "default";
  @Input() public variant: "app" | "public" | "guest" = "app";
  @Input() public title: string;
  @Input() public titleKey: string;
  @Input() public titleActionable = false;
  @Input() public titleActionLabelKey: string;
  @Input() public subtitle: string;
  @Input() public backLabelKey: string = "common.back";
  @Input() public brandLabel: string = "FOOTBALLER";
  @Input() public brandHref: string = "/";
  @Input() public loading = false;
  @Input() public loadingTitleKey: string = "common.loadingData";
  @Input() public loadingSubtitleKey: string;
  @Input() public hideContentWhileLoading = false;
  @Input() public error = false;
  @Input() public errorIcon: string;
  @Input() public errorTitle: string;
  @Input() public errorTitleKey: string = "common.error";
  @Input() public errorText: string;
  @Input() public errorTextKey: string;
  @Input() public empty = false;
  @Input() public emptyIcon: string;
  @Input() public emptyTitle: string;
  @Input() public emptyTitleKey: string;
  @Input() public emptyText: string;
  @Input() public emptyTextKey: string;
  @Input() public primaryActionLabel: string;
  @Input() public primaryActionLabelKey: string;
  @Input() public showHeader = true;
  @Input() public screenRole: string = "main";
  @Output() public back = new EventEmitter<void>();
  @Output() public titleAction = new EventEmitter<void>();
  @Output() public primaryAction = new EventEmitter<void>();

  private headerActionsObserver: ResizeObserver;

  constructor(private readonly ngZone: NgZone) {}

  @ViewChild("headerActions")
  public set headerActions(element: ElementRef<HTMLElement> | undefined) {
    this.headerActionsObserver?.disconnect();
    if (!element) {
      return;
    }
    const actions = element.nativeElement;
    const reserveActionSpace = () => {
      // Reserve the same space on both sides, including translated action labels.
      actions.parentElement.style.setProperty("--screen-header-actions-width",
        `${Math.ceil(actions.getBoundingClientRect().width)}px`);
    };
    reserveActionSpace();
    this.ngZone.runOutsideAngular(() => {
      // Only layout styles change; no Angular state or render tick is needed.
      this.headerActionsObserver = new ResizeObserver(reserveActionSpace);
      this.headerActionsObserver.observe(actions);
    });
  }

  public ngOnDestroy(): void {
    this.headerActionsObserver?.disconnect();
  }

  @HostBinding("class")
  public get hostClasses(): string {
    return this.normalizeContentClasses(this.contentClass)
      .filter((className) => !this.isLayoutUtilityClass(className))
      .join(" ");
  }

  public resolvedTitle(): string {
    return this.title || "";
  }

  public isPublicVariant(): boolean {
    return this.variant === "public" || this.variant === "guest";
  }

  private normalizeContentClasses(value: string | string[] | Set<string> | {[klass: string]: any}): string[] {
    if (!value) {
      return [];
    }
    if (typeof value === "string") {
      return value.split(/\s+/).filter(Boolean);
    }
    if (Array.isArray(value)) {
      const classes: string[] = [];
      value.forEach((entry) => classes.push(...this.normalizeContentClasses(entry)));
      return classes;
    }
    if (value instanceof Set) {
      const classes: string[] = [];
      value.forEach((entry) => classes.push(...this.normalizeContentClasses(entry)));
      return classes;
    }
    return Object.keys(value).filter((className) => !!value[className]);
  }

  private isLayoutUtilityClass(className: string): boolean {
    return className === "container"
      || className === "container-fluid"
      || className === "row"
      || className === "col"
      || className === "d-flex"
      || className === "flex-column"
      || className.indexOf("col-") === 0
      || className.indexOf("m-") === 0
      || className.indexOf("mt-") === 0
      || className.indexOf("mb-") === 0
      || className.indexOf("my-") === 0
      || className.indexOf("p-") === 0
      || className.indexOf("pt-") === 0
      || className.indexOf("pb-") === 0
      || className.indexOf("py-") === 0
      || className.indexOf("text-") === 0;
  }
}
